import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Clickable } from 'foundations-components/transitional/components/Clickable';
import { DownIcon, RightIcon } from 'foundations-assets';

// Styling below based on https://git.hubteam.com/HubSpot/foundations-components/blob/master/foundations-components/static-1.5784/js/transitional/components/SideNav/SideNavItem.tsx
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const baseNavItemStyles = ({
  theme
}) => {
  return Object.assign({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    paddingInlineStart: theme.space['100'],
    position: 'relative',
    transition: '0.15s all ease-out',
    borderRadius: theme.borderRadius['100']
  }, theme.typography['body-100']);
};
const activeStyles = css(["color:", ";background-color:", ";&::before{content:'';border-left:", " solid ", ";height:100%;left:0;position:absolute;top:0;border-radius:", ";}"], ({
  theme
}) => theme.color['text-core-default'], ({
  theme
}) => theme.color['fill-field-hover'], ({
  theme
}) => theme.borderWidth['300'], ({
  theme
}) => theme.color['icon-interactive-default'], ({
  theme
}) => theme.borderRadius['100']);
const StyledNavFlex = styled.div.withConfig({
  displayName: "NavItem__StyledNavFlex",
  componentId: "t803f8-0"
})(["", " ", ":hover{background-color:", ";}"], baseNavItemStyles, ({
  $active
}) => $active ? activeStyles : undefined, ({
  theme
}) => theme.color['fill-field-hover']);

// need to calcualte the left margin and padding for icon button and title individually because
// the previous standalone "clickable padding" div was getting hidden in the new design.
const IconButtonWrapper = styled.div.withConfig({
  displayName: "NavItem__IconButtonWrapper",
  componentId: "t803f8-1"
})(["margin-right:", ";margin-left:", ";border-radius:", ";:hover{background:", ";}"], ({
  theme
}) => theme.space['100'], ({
  level,
  theme
}) => level === 1 ? theme.space['100'] : `calc(${theme.space['300']} * ${level - 1})`, ({
  theme
}) => theme.borderRadius['100'], ({
  theme
}) => theme.color['fill-field-hover']);

/* @bend-expect no-styled-foundations-components */
const ClickableNavTitle = styled(Clickable).withConfig({
  displayName: "NavItem__ClickableNavTitle",
  componentId: "t803f8-2"
})(["width:100%;height:100%;display:flex;align-items:center;padding-block:", ";padding-left:", ";:hover{text-decoration:none;color:", ";}"], ({
  theme
}) => theme.space['200'], ({
  nested,
  level,
  theme
}) => nested ? `calc(${theme.space['300']} * ${level - 1} + ${theme.space['800']})` : '', ({
  theme
}) => theme.color['text-interactive-default']);
const NavItem = ({
  title,
  onClick,
  onOpenChange,
  children,
  active,
  level,
  defaultOpen,
  testId
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) {
      setIsExpanded(defaultOpen);
    }
  }, [defaultOpen]);
  const toggleExpandItem = e => {
    onOpenChange(isExpanded ? 'close' : 'open');
    setIsExpanded(current => !current);
    e.preventDefault();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(StyledNavFlex, {
      $active: active,
      "data-test-id": testId,
      children: [children &&
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(IconButtonWrapper, {
        level: level,
        children: /*#__PURE__*/_jsx(IconButton, {
          use: "transparent",
          size: "xs",
          onClick: toggleExpandItem,
          "data-test-id": `${testId}-toggle`,
          children: isExpanded ? /*#__PURE__*/_jsx(DownIcon, {}) : /*#__PURE__*/_jsx(RightIcon, {})
        })
      }), /*#__PURE__*/_jsx(ClickableNavTitle, {
        "data-test-id": `${testId}-title`,
        level: level,
        nested: !children,
        onClick: onClick,
        children: title
      })]
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: isExpanded,
      children: children
    })]
  });
};
export default NavItem;